import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import type { EventView, PastEventsResponse } from '@grc/shared';
import { ErrorLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { t } from '../../i18n';
import { byDay, clubDate } from './dates';
import { EventDays } from './EventList';
import { pastEvents, upcomingEvents } from './events-api';
import { Unloaded, useLoad } from './Load';

/**
 * The Events tab (B3; D22, D34), replacing its "Coming soon": its heading, the switch between its
 * three views (the owner's answer, 3A), and the view: Upcoming at /events, Past at /events/past,
 * Month at /events/month (Month.tsx). Each is a route of its own, so Back and a reload keep it, and
 * the tab stays lit on all of them and on an event's page (EventPage.tsx).
 */
export function EventsTab() {
  return (
    <section className="screen">
      <h1>{t('nav.events')}</h1>
      <nav className="views" aria-label={t('events.views')}>
        <ul>
          <li>
            <NavLink to="/events" end>
              {t('events.upcoming')}
            </NavLink>
          </li>
          <li>
            <NavLink to="/events/past">{t('events.past')}</NavLink>
          </li>
          <li>
            <NavLink to="/events/month">{t('events.month')}</NavLink>
          </li>
        </ul>
      </nav>
      <Outlet />
    </section>
  );
}

/** Upcoming: every event not over yet (the run under way too), by day, soonest first (D33). */
export function Upcoming() {
  // The screen's moment, for Today and Tomorrow: as it opened.
  const [now] = useState(() => Date.now());
  const { state, retry } = useLoad(upcomingEvents);
  if (state.status !== 'loaded') return <Unloaded state={state} onRetry={retry} />;
  if (state.value.length === 0) return <p className="muted">{t('events.none')}</p>;
  return <EventDays days={byDay(state.value, 'soonest')} today={clubDate(now)} from="/events" />;
}

function firstPastPage(): Promise<PastEventsResponse> {
  return pastEvents();
}

/**
 * Past: the events that are over, the newest day first, each day's events in time order (the
 * owner's answer, 3A), 50 at a time (B2's pages), Show older events for the page before.
 */
export function Past() {
  const { state, retry } = useLoad(firstPastPage);
  if (state.status !== 'loaded') return <Unloaded state={state} onRetry={retry} />;
  return <PastList first={state.value} />;
}

function PastList({ first }: { first: PastEventsResponse }) {
  const [now] = useState(() => Date.now());
  const [events, setEvents] = useState<EventView[]>(first.events);
  const [more, setMore] = useState(first.more);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function showOlder() {
    // The next page is the one before the oldest event shown: the last of the pages so far.
    const oldest = events[events.length - 1];
    if (busy || oldest === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      const page = await pastEvents(oldest.id);
      setEvents((shown) => [...shown, ...page.events]);
      // A page with nothing in it ends the list, whatever it says.
      setMore(page.more && page.events.length > 0);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <>
      {events.length === 0 ? (
        <p className="muted">{t('events.noPast')}</p>
      ) : (
        <EventDays days={byDay(events, 'newest')} today={clubDate(now)} from="/events/past" />
      )}
      {more && (
        <div className="stack event-more">
          <ErrorLine text={error} />
          <button
            type="button"
            className="button button-secondary"
            onClick={() => void showOlder()}
            disabled={busy}
          >
            {busy ? t('app.loading') : t('events.older')}
          </button>
        </div>
      )}
    </>
  );
}
